"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import { createPortal } from "react-dom";
import { SafeImage } from "@/components/ui/safe-image";
import type { ProductImage } from "@/types";
import { cn, mediaUrl } from "@/lib/utils";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

interface ProductImagesProps {
  images: ProductImage[];
  name: string;
  activeUrl?: string;
}

export function ProductImages({ images, name, activeUrl }: ProductImagesProps) {
  const sorted = useMemo(
    () =>
      [...images].sort((a, b) => {
        if (a.isCover) return -1;
        if (b.isCover) return 1;
        return a.sortOrder - b.sortOrder;
      }),
    [images]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [variantUrl, setVariantUrl] = useState<string | undefined>(activeUrl);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setIsTouchDevice(window.matchMedia("(pointer: coarse)").matches);
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    setVariantUrl(activeUrl);
  }, [activeUrl]);

  const mainUrl = variantUrl ?? sorted[selectedIndex]?.url;
  const currentIndex = variantUrl
    ? sorted.findIndex((img) => img.url === variantUrl)
    : selectedIndex;
  const mainImage = sorted[currentIndex];
  const mainObjectPosition = `${mainImage?.focalX ?? 50}% ${mainImage?.focalY ?? 50}%`;

  const handleThumbnailClick = (i: number) => {
    setSelectedIndex(i);
    setVariantUrl(undefined);
  };

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setSelectedIndex(currentIndex - 1);
      setVariantUrl(undefined);
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < sorted.length - 1) {
      setSelectedIndex(currentIndex + 1);
      setVariantUrl(undefined);
    }
  }, [currentIndex, sorted.length]);

  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const threshold = 50;
      if (info.offset.x > threshold) {
        handlePrev();
      } else if (info.offset.x < -threshold) {
        handleNext();
      }
    },
    [handlePrev, handleNext]
  );

  // Keyboard nav for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, handlePrev, handleNext]);

  if (!mainUrl && sorted.length === 0) {
    return (
      <div className="aspect-square bg-gray-100 rounded-[var(--sf-radius)] flex items-center justify-center text-gray-300">
        <svg className="w-24 h-24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Ana görsel — hover zoom + lightbox click */}
      <motion.div
        className="relative aspect-[3/4] bg-gray-50 rounded-[var(--sf-radius)] overflow-hidden touch-pan-y select-none group cursor-zoom-in"
        drag={isTouchDevice ? "x" : false}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        onClick={() => setLightboxOpen(true)}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={mainUrl}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3, ease: "easeOut" }}
            className="absolute inset-0 transition-transform duration-500 md:group-hover:scale-110"
          >
            <SafeImage
              src={mainUrl ? mediaUrl(mainUrl) : undefined}
              alt={name}
              fill
              priority
              placeholder="blur"
              blurDataURL="data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw=="
              className="object-cover"
              style={{ objectPosition: mainObjectPosition }}
              sizes="(max-width: 768px) 100vw, 50vw"
              fallbackClassName="bg-gray-50 text-gray-300"
            />
          </motion.div>
        </AnimatePresence>

        {/* Zoom indicator */}
        <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:flex">
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white/90 text-gray-500 shadow-sm backdrop-blur-sm border border-gray-200">
            <ZoomIn className="w-4 h-4" />
          </div>
        </div>

        {/* Swipe ok indicator'ları — sadece touch cihazlarda ve çoklu görsel varsa */}
        {isTouchDevice && sorted.length > 1 && (
          <>
            {currentIndex > 0 && (
              <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10 pointer-events-none flex items-center justify-center w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm">
                <ChevronLeft className="w-5 h-5 text-white/80" />
              </div>
            )}
            {currentIndex < sorted.length - 1 && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10 pointer-events-none flex items-center justify-center w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm">
                <ChevronRight className="w-5 h-5 text-white/80" />
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* Thumbnails */}
      {sorted.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sorted.map((img, i) => (
            <button
              key={img.id}
              onClick={() => handleThumbnailClick(i)}
              className={cn(
                "relative flex-shrink-0 w-16 h-16 rounded-lg border-2 overflow-hidden transition-all",
                !variantUrl && i === selectedIndex
                  ? "border-[var(--sf-primary)] ring-2 ring-[var(--sf-primary)]/20"
                  : "border-transparent hover:border-gray-300"
              )}
            >
              <SafeImage
                src={mediaUrl(img.url)}
                alt={img.altText ?? `${name} ${i + 1}`}
                fill
                className="object-cover"
                style={{ objectPosition: `${img.focalX ?? 50}% ${img.focalY ?? 50}%` }}
                sizes="64px"
                fallbackClassName="bg-gray-50 text-gray-300"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && typeof window !== "undefined" &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
              onClick={() => setLightboxOpen(false)}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 shrink-0">
                <span className="text-sm text-white/60">
                  {currentIndex + 1} / {sorted.length}
                </span>
                <button
                  onClick={() => setLightboxOpen(false)}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                  aria-label="Kapat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Main image area */}
              <div
                className="flex-1 flex items-center justify-center relative overflow-hidden min-h-0"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Prev button */}
                {currentIndex > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                    aria-label="Önceki"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}

                {/* Image */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={mainUrl}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.2 }}
                    className="relative w-full h-full"
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.1}
                    onDragEnd={handleDragEnd}
                  >
                    <SafeImage
                      src={mainUrl ? mediaUrl(mainUrl) : undefined}
                      alt={name}
                      fill
                      className="object-contain"
                      sizes="100vw"
                      fallbackClassName="text-white/20"
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Next button */}
                {currentIndex < sorted.length - 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleNext(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                    aria-label="Sonraki"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* Thumbnails */}
              {sorted.length > 1 && (
                <div className="px-4 py-4 shrink-0">
                  <div className="flex gap-2 justify-center overflow-x-auto">
                    {sorted.map((img, i) => (
                      <button
                        key={img.id}
                        onClick={(e) => { e.stopPropagation(); handleThumbnailClick(i); }}
                        className={cn(
                          "relative flex-shrink-0 w-14 h-14 rounded-lg border-2 overflow-hidden transition-all",
                          i === currentIndex
                            ? "border-white ring-2 ring-white/30"
                            : "border-white/20 hover:border-white/50 opacity-60 hover:opacity-100"
                        )}
                      >
                        <SafeImage
                          src={mediaUrl(img.url)}
                          alt={img.altText ?? `${name} ${i + 1}`}
                          fill
                          className="object-cover"
                          style={{ objectPosition: `${img.focalX ?? 50}% ${img.focalY ?? 50}%` }}
                          sizes="56px"
                          fallbackClassName="bg-gray-800 text-gray-600"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
}
